import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createDesignation,
  getDesignations,
  updateDesignation,
} from '../services/designation.service';
import {
  DesignationInsertInput,
  DesignationUpdateInput,
  GetDesignationsParams,
} from '../types/designation.types';

const invalidateDesignationQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-designations'] });

export const useGetDesignations = (params: GetDesignationsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-designations', params],
    queryFn: () => getDesignations(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateDesignation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: DesignationInsertInput) => createDesignation(input),
    onSuccess: () => {
      invalidateDesignationQueries(queryClient);
      toast({ title: t('DESIGNATION_CREATED') });
    },
  });
};

export const useUpdateDesignation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: DesignationUpdateInput }) =>
      updateDesignation(params),
    onSuccess: () => {
      invalidateDesignationQueries(queryClient);
      toast({ title: t('DESIGNATION_UPDATED') });
    },
  });
};
